import React, { useContext, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { UserContext } from '../App';
import { icon_edit, icon_trash } from './Icons';
import {
  customPageHtmlFileName,
  customPageMetadataFileName,
  CustomPageListItem,
  deleteCustomPageFile,
  listCustomPages,
} from '../util/customPages';

type CustomPageTilesProps = {
  path: string;
  canCreate?: boolean;
};

type DeleteTarget = {
  id: string;
  title: string;
  path: string;
};

function CustomPageTiles({ path, canCreate }: CustomPageTilesProps) {
  const userCtx = useContext(UserContext);
  const navigate = useNavigate();
  const [customPages, setCustomPages] = useState<CustomPageListItem[]>([]);
  const [deleteTarget, setDeleteTarget] = useState<DeleteTarget | null>(null);
  const [deleting, setDeleting] = useState(false);
  const allowCreate = canCreate ?? userCtx.user != null;
  const allowManage = userCtx.user != null;

  const loadPages = () => {
    listCustomPages(path)
      .then((data: CustomPageListItem[]) => {
        setCustomPages(data);
      })
      .catch((e: Error) => {
        console.error('Failed to load custom pages!', e);
      });
  };

  useEffect(() => {
    loadPages();
  }, [path]);

  const openDeleteConfirm = (
    pageId: string,
    pageTitle: string,
    pagePath: string,
    e: React.MouseEvent
  ) => {
    e.preventDefault();
    e.stopPropagation();
    setDeleteTarget({ id: pageId, title: pageTitle, path: pagePath });
  };

  const confirmDelete = () => {
    if (!deleteTarget) {
      return;
    }

    setDeleting(true);
    Promise.all([
      deleteCustomPageFile(deleteTarget.path, customPageMetadataFileName(deleteTarget.id)),
      deleteCustomPageFile(deleteTarget.path, customPageHtmlFileName(deleteTarget.id)),
    ])
      .then(() => {
        setDeleteTarget(null);
        loadPages();
      })
      .catch((e: Error) => {
        console.error('Failed to delete custom page!', e);
      })
      .finally(() => {
        setDeleting(false);
      });
  };

  return (
    <>
      {customPages.map((page) => {
        const pageId = page.id;
        const pagePath = page.path || path;
        if (!pageId || !pagePath) {
          return null;
        }
        const title = page.title || 'Stránka';
        return (
          <Link key={pageId} className="link relative" to={'/custom' + pagePath + '/' + pageId}>
            <span>{title}</span>
            {allowManage && (
              <div className="absolute bottom-2 left-2 right-2 flex justify-center gap-2">
                <button
                  type="button"
                  className="flex h-10 w-10 items-center justify-center rounded-full bg-white/90 text-slate-700 shadow transition hover:bg-white hover:text-slate-900"
                  aria-label="Upraviť"
                  title="Upraviť"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    navigate(
                      '/custom/edit/' + pageId + '?path=' + encodeURIComponent(pagePath)
                    );
                  }}
                >
                  {icon_edit}
                </button>
                <button
                  type="button"
                  className="flex h-10 w-10 items-center justify-center rounded-full bg-red-600/90 text-white shadow transition hover:bg-red-600"
                  aria-label="Vymazať"
                  title="Vymazať"
                  onClick={(e) => openDeleteConfirm(pageId, title, pagePath, e)}
                >
                  {icon_trash}
                </button>
              </div>
            )}
          </Link>
        );
      })}
      {allowCreate && (
        <Link
          id="custom-create"
          className="link"
          to={'/custom/edit?path=' + encodeURIComponent(path)}
        >
          <span>+ Vytvoriť stránku</span>
        </Link>
      )}

      {deleteTarget && (
        <>
          <div className="justify-center items-center flex overflow-x-hidden overflow-y-auto fixed inset-0 z-50 outline-none focus:outline-none">
            <div className="relative w-auto my-6 mx-auto max-w-3xl">
              <div className="border-0 rounded-lg shadow-lg relative flex flex-col w-full bg-white outline-none focus:outline-none">
                <div className="flex items-start justify-between p-5 border-b border-solid border-slate-200 rounded-t">
                  <h3 className="text-2xl font-semibold">
                    Potvrdenie
                  </h3>
                  <button
                    className="p-1 ml-auto bg-transparent border-0 text-black opacity-5 float-right text-3xl leading-none font-semibold outline-none focus:outline-none"
                    onClick={() => setDeleteTarget(null)}
                  >
                    <span className="bg-transparent text-black opacity-5 h-6 w-6 text-2xl block outline-none focus:outline-none">
                      ×
                    </span>
                  </button>
                </div>

                <div className="relative p-6 flex-auto">
                  Naozaj chcete vymazať stránku {deleteTarget.title}?
                </div>

                <div className="flex items-center justify-end p-6 border-t border-solid border-slate-200 rounded-b">
                  <button
                    className="btn-secondary"
                    type="button"
                    onClick={() => setDeleteTarget(null)}
                    disabled={deleting}
                  >
                    Zrušiť
                  </button>
                  <button
                    className="btn-primary ml-2"
                    type="button"
                    onClick={confirmDelete}
                    disabled={deleting}
                  >
                    {deleting ? 'Mažem...' : 'Vymazať'}
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div className="opacity-25 fixed inset-0 z-40 bg-black"></div>
        </>
      )}
    </>
  );
}

export default CustomPageTiles;
